import React, { useState, useEffect, useMemo } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	useToast,
	Spinner,
	Box,
	Flex,
	HStack,
	VStack,
	Text,
	Badge,
	Icon,
	IconButton,
	Skeleton,
} from '@chakra-ui/react';
import { FaCrown, FaCheckCircle, FaInfinity } from 'react-icons/fa';
import { Elements } from '@stripe/react-stripe-js';
import getStripePromise from '../../../../utils/stripePromise';
import EmbeddedLifetimeCheckout from './EmbeddedLifetimeCheckout';
import { useAuthenticatedUserInfo } from '../../../../services/Contexts/AuthenticatedUserInfoContext';
import { useStripePrice } from '../../../../hooks/useStripe';
import { createLifetimePaymentIntent } from '../../../../Api/lifetimeSubscriptionApi';

const stripePromise = getStripePromise();

const parseStripePrice = (stripePrice) => {
	if (!stripePrice) return null;

	let priceObj = null;
	if (
		stripePrice.data?.data &&
		typeof stripePrice.data.data === 'object' &&
		stripePrice.data.data.unit_amount != null
	) {
		priceObj = stripePrice.data.data;
	} else if (stripePrice.data?.unit_amount != null) {
		priceObj = stripePrice.data;
	} else if (stripePrice.unit_amount != null) {
		priceObj = stripePrice;
	} else {
		return null;
	}

	const unitAmount = priceObj.unit_amount;
	if (unitAmount == null || Number.isNaN(unitAmount)) return null;

	const amount = unitAmount / 100;
	const currency = (priceObj.currency || 'usd').toUpperCase();
	const symbol = currency === 'USD' ? '$' : `${currency} `;

	return {
		formatted: `${symbol}${amount.toLocaleString(undefined, {
			minimumFractionDigits: currency === 'USD' ? 0 : 2,
			maximumFractionDigits: currency === 'USD' ? 0 : 2,
		})}`,
	};
};

const LifetimeCheckoutModal = ({
	isOpen,
	onClose,
	plan,
	onPurchaseSuccess,
	deferSubscriptionRefresh = false,
}) => {
	const toast = useToast();
	const { userId, userEmail, companyId, token } = useAuthenticatedUserInfo();
	const [clientSecret, setClientSecret] = useState('');
	const [paymentIntentId, setPaymentIntentId] = useState('');
	const [isLoadingIntent, setIsLoadingIntent] = useState(false);

	const stripePriceId = plan?.stripePriceIdOneTime || null;
	const { data: stripePrice, isLoading: priceLoading } = useStripePrice(stripePriceId);
	const priceInfo = useMemo(() => parseStripePrice(stripePrice), [stripePrice]);

	useEffect(() => {
		if (!isOpen) {
			setClientSecret('');
			setPaymentIntentId('');
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !plan || !companyId || !userEmail) return;

		const initPayment = async () => {
			setIsLoadingIntent(true);
			setClientSecret('');
			setPaymentIntentId('');

			try {
				const response = await createLifetimePaymentIntent(
					{
						companyId,
						userId,
						userEmail,
						planId: plan._id || plan.id,
					},
					token,
				);

				setClientSecret(response.data.clientSecret);
				setPaymentIntentId(response.data.paymentIntentId);
			} catch (error) {
				toast({
					title: 'Checkout unavailable',
					description:
						error.response?.data?.error ||
						'Could not start lifetime checkout. Check the Stripe one-time price on this plan.',
					status: 'error',
					duration: 6000,
					isClosable: true,
				});
				onClose();
			} finally {
				setIsLoadingIntent(false);
			}
		};

		initPayment();
	}, [isOpen, plan, companyId, userEmail, userId, token, toast, onClose]);

	const handleSuccess = (data) => {
		setClientSecret('');
		setPaymentIntentId('');
		onClose();
		onPurchaseSuccess?.(data);
	};

	const handleClose = () => {
		setClientSecret('');
		setPaymentIntentId('');
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size={{ base: 'full', md: '3xl' }}
			isCentered
			scrollBehavior='inside'
			closeOnOverlayClick={!isLoadingIntent}
			closeOnEsc={!isLoadingIntent}>
			<ModalOverlay bg='blackAlpha.600' backdropFilter='blur(4px)' />
			<ModalContent mx={4} my={{ base: 0, md: 4 }} borderRadius={{ base: 0, md: '2xl' }} overflow='hidden'>
				{plan && (
					<Box
						bgGradient='linear(135deg, #7c2d12 0%, #c2410c 50%, #ea580c 100%)'
						px={6}
						py={6}
						color='white'
						position='relative'>
						<Flex align='start' justify='space-between' gap={4}>
							<HStack align='start' spacing={4}>
								<Flex
									align='center'
									justify='center'
									w={12}
									h={12}
									borderRadius='xl'
									bg='whiteAlpha.200'
									flexShrink={0}>
									<Icon as={FaInfinity} boxSize={5} />
								</Flex>
								<Box>
									<HStack spacing={2} mb={2}>
										<Icon as={FaCrown} color='yellow.200' boxSize={3} />
										<Badge bg='whiteAlpha.200' color='white' borderRadius='full' px={2}>
											Lifetime access
										</Badge>
									</HStack>
									<Text as='h2' fontSize='2xl' fontWeight='800' m={0} mb={2}>
										{plan.planName}
									</Text>
									{priceLoading ? (
										<Skeleton height='18px' width='80px' />
									) : priceInfo ? (
										<Text fontSize='sm' m={0} fontWeight='600'>
											{priceInfo.formatted}
											<Text as='span' opacity={0.85}>
												{' '}
												one-time
											</Text>
										</Text>
									) : null}
								</Box>
							</HStack>
							<IconButton
								aria-label='Close'
								icon={
									<Text fontSize='lg' lineHeight={1}>
										×
									</Text>
								}
								variant='ghost'
								color='white'
								_hover={{ bg: 'whiteAlpha.200' }}
								onClick={handleClose}
								isDisabled={isLoadingIntent}
							/>
						</Flex>
					</Box>
				)}

				<ModalBody px={{ base: 4, md: 6 }} py={5}>
					<Box
						mb={4}
						p={3}
						borderRadius='lg'
						bg='orange.50'
						border='1px solid'
						borderColor='orange.100'>
						<HStack spacing={2}>
							<Icon as={FaCheckCircle} color='orange.500' />
							<Text fontSize='xs' color='orange.900' m={0}>
								One payment. Lifetime platform access at this tier&apos;s limits.
							</Text>
						</HStack>
					</Box>

					{isLoadingIntent || !clientSecret ? (
						<VStack spacing={4} py={8}>
							<Spinner size='lg' color='orange.400' />
							<Text color='gray.600' m={0}>
								Preparing secure checkout…
							</Text>
						</VStack>
					) : (
						<Elements
							stripe={stripePromise}
							options={{
								clientSecret,
								appearance: {
									theme: 'stripe',
									variables: {
										colorPrimary: '#ea580c',
										borderRadius: '8px',
									},
								},
							}}>
							<EmbeddedLifetimeCheckout
								plan={plan}
								paymentIntentId={paymentIntentId}
								onSuccess={handleSuccess}
								onCancel={handleClose}
								deferSubscriptionRefresh={deferSubscriptionRefresh}
							/>
						</Elements>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

export default LifetimeCheckoutModal;
